/* globals document */
import * as React from 'react';
import {useIsomorphicLayoutEffect} from '@packages/systems/spring-hooks';

import {css} from '../emotion/emotion';

import {
  colors,
  coreColorVars,
  generateTokensFromObject,
  /**
   * Theme objects
   */
  lightTheme,
  darkBrighterTheme,
  darkDarkerTheme,
  darkDefaultTheme,
} from '@packages/utilities/styled-tokens';
import type {SpringProviderUnits} from '@packages/utilities/styled-tokens';

type ThemeContextProps = {
  /**
   * Communicates what theme is currently active.
   * Internal use only.
   */
  __internalTheme?: string;
  /**
   * An incoming CSS className.
   */
  className?: string;
  /**
   * Determines if the ThemeProvider is the root level ThemeProvider.
   * Informs ThemeProvider based on nested ThemeProvider rendering.
   */
  isRootProvider: boolean | null;
  /**
   * The CSS className generated by emotionStyled's css compiler based on
   * the theme object.
   */
  themeClassName?: string;
};

/**
 * Converts a set of theme tokens into CSS Vars assigned to a className.
 */
export const createTheme = (theme: any): ReturnType<typeof css> => {
  const {setTokens} = generateTokensFromObject(theme);

  // Convert cssVars into a className
  const className = css(setTokens);
  return className;
};

export const THEMES = {
  light: createTheme(lightTheme),
  darkBrighter: createTheme(darkBrighterTheme),
  darkDarker: createTheme(darkDarkerTheme),
  darkDefault: createTheme(darkDefaultTheme),
} as const;

const FALLBACK_THEME: keyof typeof THEMES = 'light';

const resolveThemeName = (
  themeName: ThemeProviderProps['theme'] | string | undefined
): keyof typeof THEMES | undefined => {
  if (themeName === undefined) return undefined;
  if (Object.prototype.hasOwnProperty.call(THEMES, themeName)) {
    return themeName as keyof typeof THEMES;
  }

  return FALLBACK_THEME;
};

export type ThemeProviderProps = {
  /**
   * The element to render.
   */
  children?: any;
  /**
   * id used for Jest and Cypress test identification.
   */
  ['data-automation-id']?: string;
  /**
   * The desired theme.
   */
  theme?: keyof typeof THEMES;
  /**
   * The units context.
   */
  units?: SpringProviderUnits;
  /**
   * Adds core CSS colors into the Theme wrapper div.
   * This is useful when we are rendering Spring somewhere we don't want to inject our core colors onto root (e.g. Editor)
   */
  _addCoreColorsOnTheme?: boolean;
  /**
   * Determines if the theme tokens should be rendered at the
   * `:root` level.
   */
  setRoot?: boolean;
  /**
   * Determines if the base background and text colors should be rendered
   * by ThemeProvider.
   */
  setBackgroundAndColorOnRoot?: boolean;
};

/**
 * In order to integrate Spring's ThemeProvider safely with all environments,
 * we need to provide the opportunity to OPT OUT of setting the global
 * background and text color.
 *
 * This is to ensure that our global styles (mostly CSS variables) is
 * compatible with environments that also render user content (i.e. Editor)
 * and/or existing background and text colors.
 *
 * @see https://webflow.atlassian.net/browse/PDTY-3684
 *
 * 2023-01-10
 */
const backgroundInitialStyles = css({backgroundColor: 'initial'});
const backgroundThemedStyles = css({
  backgroundColor: colors.background1,
  color: colors.textPrimary,
});
const coreColorVarsStyles = css(coreColorVars);

/**
 * ### About
 *
 * This ensures that portaled components can correctly absorb the CSS vars
 * coming from the theme.
 *
 * ### Taking care of portals
 *
 * The current implementation of our ThemeProvider scopes the CSS vars locally
 * to a wrapper div. This works very well to provide token values
 * (via CSS variables) to inner elements. However, portalling typically moves
 * elements (such as Modals, Popovers, and Tooltips) outside of this `div`
 * scope - rendering them much closer to the document.body level.
 *
 * ### Nesting
 *
 * This solution only works for the first rendered theme. For example, if
 * there's a (very unadvised) scenario where we need to jump between
 * Light > Dark > Light > Dark... Only the first Light theme variables
 * will be inserted at the document.body level.
 *
 * ### Room for improvement
 *
 * That being said, we may run into issues for Documentation site / code example
 * based rendering. For example, rendering a Popover on the Spring documentation
 * site and switching it from light to dark theme in the live code previewer.
 */
export const useThemeOnRoot = ({
  className,
  isRootProvider: isRootProviderProp,
  namespace = 'wf-spring-theme-render',
  setRoot = false,
  theme,
}: {
  className?: string;
  isRootProvider?: boolean | null;
  namespace?: string;
  setRoot?: boolean;
  theme?: ThemeProviderProps['theme'];
} = {}) => {
  useIsomorphicLayoutEffect(() => {
    if (!document.body || !setRoot || !className) return;

    const isNotRootProvider =
      typeof isRootProviderProp === 'boolean' && !isRootProviderProp;

    if (isNotRootProvider) return;

    const dataNamespace = `data-${namespace}`;
    const didInsertThemeVars = document.body.getAttribute(dataNamespace);

    if (didInsertThemeVars) return;

    document.body.classList.add(className);
    document.body.setAttribute(dataNamespace, className);
    if (theme) {
      document.body.setAttribute('data-theme', theme);
    }

    /**
     * This cleanup function is critical to (global) theme switching.
     * For example, this is used in Designer's Appearance Settings feature.
     * @see https://webflow.com/updates/adjust-appearance-settings
     */
    return () => {
      if (!document.body || !className) return;
      if (isNotRootProvider) return;

      document.body.classList.remove(className);
      /**
       * Removing insertion state for the root level ThemeProvider.
       */
      document.body.removeAttribute(dataNamespace);
      // @ts-expect-error - TS2554 - Expected 1 arguments, but got 2.
      document.body.removeAttribute('data-theme', theme);
    };
  }, [className, setRoot, theme, isRootProviderProp]);
};

const useCoreColorsOnRoot = (enabled?: boolean) => {
  useIsomorphicLayoutEffect(() => {
    if (!document.body || !enabled) return;
    if (document.body.classList.contains(coreColorVarsStyles)) return;

    document.body.classList.add(coreColorVarsStyles);
  }, [enabled]);
};

const useBackgroundAndColorOnRoot = (enabled?: boolean) => {
  useIsomorphicLayoutEffect(() => {
    if (!document.body || !enabled) return;
    if (document.body.classList.contains(backgroundThemedStyles)) return;

    document.body.classList.add(backgroundThemedStyles);
  }, [enabled]);
};

/**
 * # ThemeContext
 *
 * A tiny communication layer for the `ThemeProvider` to work with potentially
 * portalled elements.
 *
 * This extra mechanism is needed because our theme values (tokens) are powered
 * via CSS variables, which are scoped based on the order of elements in the
 * DOM.
 *
 * For portalling to work with theme values, we'll need to render a
 * `ThemeProvider`, wrapping the portalled content.
 *
 * @example
 * ```jsx
 * <ThemeProvider theme="light">
 *  ...
 *   <Popover>
 *    <Portal>
 *      <ThemeProvider>
 *        <PopoverContent>
 *          ...
 *        </PopoverContent>
 *      </ThemeProvider>
 *    </Portal>
 *   </Popover>
 *  ...
 * </ThemeProvider>
 * ```
 *
 * The resulting HTML may look like this:
 * @example
 * ```html
 * <body>
 *   <div class="wf-1jcaj"> // This is the root ThemeProvider
 *     ...
 *     <button>Popover</button>
 *     ...
 *   </div>
 *   <div class="portal">
 *     <div class="wf-1jcaj">  // This is the portalled ThemeProvider
 *       <div class="popover-content">
 *         ...
 *       </div>
 *     </div>
 *   </div>
 * </body>
 * ```
 */
const ThemeContext = React.createContext<ThemeContextProps>({
  __internalTheme: 'light',
  isRootProvider: null,
  themeClassName: THEMES['light'],
  className: THEMES['light'],
});

/**
 * This nullifies the (DOM) layout effects of the wrapper `div` element
 * for the SizeContextProvider.
 */
const stylesToNullifyDOMNode = {display: 'contents'} as const;

/**
 * Retrieves data from the Spring ThemeContext.
 *
 * @returns {ThemeContextProps}
 */
export const useThemeContext = (): ThemeContextProps =>
  React.useContext(ThemeContext);

/**
 * # ThemeProvider
 *
 * Defines the theme (e.g. `light`/`dark`) for the application or scoped parts
 * of the application.
 *
 * # Usage
 *
 * The Spring `ThemeProvider` is usually rendered at the root-level of your
 * application, along with any other providers you may have.
 *
 * ```jsx
 * export const App = ({ children }) => {
 *   return (
 *     <ThemeProvider theme="light">
 *       <StateManagementProvider>
 *         <AnotherProvider>{children}</AnotherProvider>
 *       </StateManagementProvider>
 *     </ThemeProvider>
 *   );
 * };
 * ```
 */
export const ThemeProvider = React.forwardRef<
  HTMLDivElement,
  ThemeProviderProps
>(
  (
    {
      children,
      theme: themeNameProp,
      setRoot = false,
      setBackgroundAndColorOnRoot = true,
      _addCoreColorsOnTheme = false,
    },
    ref
  ) => {
    const themeName = resolveThemeName(themeNameProp);
    const themeContextValue = useThemeContext();
    /**
     * Starts with null (if root). Otherwise, it's set as a boolean (false) for
     * nested ThemeProviders.
     */
    const isRootProvider = themeContextValue.isRootProvider === null;

    /**
     * This is the secret sauce that allows for ThemeProvier to (recursively)
     * inherit from a parent ThemeProvider, if one exists.
     *
     * In the very rare (but possible) chance that we need to do something
     * special with the ThemeProvider at the root level...
     *
     * @see https://github.com/webflow/webflow/pull/46830#discussion_r858088330
     */
    const shouldNotInheritContext = Boolean(themeName);

    const themeClassName = themeName
      ? THEMES[themeName]
      : themeContextValue.themeClassName;

    /**
     * For ThemeProvider usage that's used exclusively to inherit from
     * other ThemeProviders (e.g. usage for Portal), we need to nullify
     * the backgroundColor that's rendered.
     *
     * This is important, otherwise, the Portal'ed element will have colored
     * background in their surrounding `div`.
     */
    const className = shouldNotInheritContext
      ? themeClassName
      : [themeContextValue.className, backgroundInitialStyles].join(' ');

    useThemeOnRoot({
      className,
      isRootProvider,
      setRoot,
      theme: themeName,
    });

    useBackgroundAndColorOnRoot(setBackgroundAndColorOnRoot);
    useCoreColorsOnRoot(_addCoreColorsOnTheme);

    /**
     * Introducing a render prop pattern here for children.
     * This is to help accommodate portal rendering where we may not want
     * an div wrapper, which may affect layout for portalled elements, such
     * as the various layers of a modal (i.e. backdrop > wrapper > modal).
     */
    const isRenderProp = typeof children === 'function';

    const dataTheme = themeName || themeContextValue.__internalTheme;

    const renderProps = {
      className,
      'data-theme': dataTheme,
      ref,
    } as const;

    const contextProps = React.useMemo(
      () => ({
        __internalTheme: dataTheme,
        className,
        isRootProvider,
        themeClassName,
      }),
      [dataTheme, className, isRootProvider, themeClassName]
    );

    return (
      <ThemeContext.Provider value={contextProps}>
        {isRenderProp ? (
          children(renderProps)
        ) : (
          <div {...renderProps} style={stylesToNullifyDOMNode}>
            {children}
          </div>
        )}
      </ThemeContext.Provider>
    );
  }
);
